import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { fetchMultisigGroupByAccount, MultisigProposalStatus } from '@/api'
import { Breadcrumbs } from '@/common/Breadcrumbs'
import InfiniteList, { InfiniteListLoadingStates } from '@/common/InfiniteList'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatAddress } from '@/helpers/formatters'
import { queryKeys } from '@/queries/keys'
import { RoutePaths } from '@/routes'
import UiAddressCopy from '@/ui/UiAddressCopy'
import { UiBadge } from '@/ui/UiBadge'
import { UiButton } from '@/ui/UiButton'
import { UiCard } from '@/ui/UiCard'
import UiContainer from '@/ui/UiContainer'
import {
  UiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'
import { UiSeparator } from '@/ui/UiSeparator'
import { UiSkeleton } from '@/ui/UiSkeleton'

import ProposalCard from './components/ProposalCard'
import ProposalCardSkeleton from './components/ProposalCardSkeleton'
import { useMultisigProposalsInfiniteList } from './hooks/multisig-proposals-infinite-list'

export default function AdminGroup() {
  const { address } = useKeplrWallet()
  const { account } = useParams<{ account: string }>()
  const [statusFilter, setStatusFilter] = useState<
    MultisigProposalStatus | 'ALL'
  >('ALL')

  const groupQuery = useQuery({
    queryKey: queryKeys.multisig.groupByAccount(account),
    enabled: Boolean(account),
    queryFn: () => fetchMultisigGroupByAccount(account!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const proposalsQuery = useMultisigProposalsInfiniteList({
    pagination: {
      limit: '20',
    },
    queryOptions: {
      enabled: Boolean(account),
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  })

  const allProposals = useMemo(
    () => proposalsQuery.data?.pages.flatMap(p => p.proposal) ?? [],
    [proposalsQuery.data],
  )

  const sortedAndFilteredProposals = useMemo(() => {
    let proposals = [...allProposals]

    // Filter by status
    if (statusFilter !== 'ALL') {
      proposals = proposals.filter(p => p.status === statusFilter)
    }

    // Sort by submitBlock desc (newer first)
    proposals.sort((a, b) => {
      const blockA = BigInt(a.submitBlock)
      const blockB = BigInt(b.submitBlock)
      return blockA > blockB ? -1 : blockA < blockB ? 1 : 0
    })

    return proposals
  }, [allProposals, statusFilter])

  const proposalsListLoadingState = (() => {
    if (proposalsQuery.isPending && allProposals.length === 0) {
      return InfiniteListLoadingStates.Initial
    }

    if (proposalsQuery.isError && allProposals.length === 0) {
      return InfiniteListLoadingStates.Error
    }

    if (proposalsQuery.isFetchingNextPage) {
      return InfiniteListLoadingStates.NextLoading
    }

    if (allProposals.length > 0 && proposalsQuery.isError) {
      return InfiniteListLoadingStates.NextError
    }

    return InfiniteListLoadingStates.Idle
  })()

  const proposalsErrorTitle = (() => {
    const error = proposalsQuery.error
    if (!error) return 'Failed to load proposals'
    if (error instanceof Error) return error.message
    return 'Failed to load proposals'
  })()

  const createProposalPath = account
    ? generatePath(RoutePaths.MultisigCreateProposal, { account })
    : RoutePaths.Multisig

  const isGroupMember = useMemo(() => {
    if (!address || !groupQuery.data) return false
    return groupQuery.data.members.some(
      member => member.toLowerCase() === address.toLowerCase(),
    )
  }, [address, groupQuery.data])

  if (!account) {
    return (
      <UiContainer>
        <div className='xs:px-4 mt-26 space-y-6 md:px-0'>
          <UiCard className='p-6 text-center'>
            <p className='text-foreground text-sm font-semibold'>
              Group account is missing
            </p>
            <p className='text-muted-foreground mt-2 text-xs'>
              Please open this page with a valid group address.
            </p>
          </UiCard>
        </div>
      </UiContainer>
    )
  }

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <div className='bg-background sticky top-0 z-10 flex flex-col gap-6 pt-2 pb-4'>
          <Breadcrumbs
            items={[
              { label: 'Home', to: RoutePaths.Root },
              { label: 'Groups', to: RoutePaths.Multisig },
              { label: formatAddress(account, 20, 10) },
            ]}
          />
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='min-w-0 flex-1 space-y-2'>
              <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
                Multisig Group
              </h1>

              {(() => {
                if (groupQuery.isPending) {
                  return <UiSkeleton className='h-4 w-70 max-w-full' />
                }

                if (groupQuery.data?.account) {
                  return (
                    <div className='flex items-center gap-2'>
                      <span className='text-muted-foreground text-sm'>
                        Group:
                      </span>
                      <UiAddressCopy
                        start={20}
                        end={10}
                        address={groupQuery.data.account}
                        className='text-foreground font-mono'
                        showFull={false}
                      />
                    </div>
                  )
                }

                return (
                  <div className='text-muted-foreground font-mono text-sm break-all'>
                    {account}
                  </div>
                )
              })()}
            </div>

            {isGroupMember && (
              <UiButton
                className='w-full rounded-full p-0 md:w-auto'
                variant='white'
              >
                <Link
                  className='flex h-full w-full items-center justify-center gap-2 p-4'
                  to={createProposalPath}
                >
                  Create proposal
                  <PlusIcon className='h-4 w-4' />
                </Link>
              </UiButton>
            )}
          </div>
        </div>

        {(() => {
          if (groupQuery.isPending) {
            return (
              <UiCard className='p-6'>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <UiSkeleton className='h-3 w-20' />
                    <UiSkeleton className='h-4 w-[320px] max-w-full' />
                  </div>

                  <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className='space-y-2'>
                        <UiSkeleton className='h-3 w-20' />
                        <UiSkeleton className='h-4 w-12' />
                      </div>
                    ))}
                  </div>
                </div>
              </UiCard>
            )
          }

          if (groupQuery.isError) {
            const message =
              groupQuery.error instanceof Error
                ? groupQuery.error.message
                : 'Failed to load group'

            return (
              <UiCard className='p-6 text-center'>
                <p className='text-foreground text-sm font-semibold'>
                  {message}
                </p>
              </UiCard>
            )
          }

          const group = groupQuery.data
          if (!group) return null

          return (
            <div className='space-y-4'>
              <UiCard className='p-6'>
                <div className='grid gap-4'>
                  <div>
                    <p className='text-muted-foreground text-xs'>
                      Group account
                    </p>
                    <UiAddressCopy
                      start={20}
                      end={10}
                      address={group.account}
                      className='font-mono'
                      showFull={false}
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
                    <div className='flex flex-col gap-1'>
                      <p className='text-muted-foreground text-xs'>Members</p>
                      <p className='text-foreground text-sm'>
                        {group.members.length}
                      </p>
                    </div>

                    <div className='flex flex-col gap-1'>
                      <p className='text-muted-foreground text-xs'>Threshold</p>
                      <p className='text-foreground text-sm'>
                        {group.threshold}
                      </p>
                    </div>
                  </div>
                </div>
              </UiCard>

              <UiCard className='p-6'>
                <div className='space-y-4'>
                  <div>
                    <h3 className='text-foreground text-base font-semibold'>
                      Members ({group.members.length})
                    </h3>
                    <p className='text-muted-foreground text-sm'>
                      All addresses that are part of this multisig group
                    </p>
                  </div>
                  <UiSeparator />
                  <div
                    className='grid max-h-[30vh] gap-3 overflow-auto pr-2'
                    role='list'
                    aria-label='Group members'
                  >
                    {group.members.map((member, index) => (
                      <div
                        key={member}
                        role='listitem'
                        className='bg-muted/30 flex items-center justify-between gap-3 rounded-lg p-3'
                      >
                        <div className='flex min-w-0 flex-1 items-center gap-3'>
                          <div className='bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold'>
                            {index + 1}
                          </div>
                          <div className='min-w-0 flex-1'>
                            <UiAddressCopy
                              start={20}
                              end={10}
                              address={member}
                              className='font-mono text-sm'
                              showFull={false}
                            />
                          </div>
                        </div>
                        {address &&
                          member.toLowerCase() === address.toLowerCase() && (
                            <UiBadge
                              variant='default'
                              size='sm'
                              className='bg-primary/10 text-primary border-primary/20 shrink-0'
                            >
                              You
                            </UiBadge>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              </UiCard>
            </div>
          )
        })()}

        {(() => {
          if (proposalsQuery.isPending && allProposals.length === 0) {
            return (
              <div className='space-y-4'>
                <div className='space-y-3'>
                  <div className='flex items-end justify-between gap-4'>
                    <div>
                      <h2 className='text-foreground text-lg font-semibold'>
                        Proposal history
                      </h2>
                      <p className='text-muted-foreground text-sm'>
                        Proposals for this multisig group
                      </p>
                    </div>
                  </div>
                  <UiSeparator />
                </div>

                <div className='space-y-4'>
                  {Array.from({ length: 12 }).map((_, index) => (
                    <ProposalCardSkeleton key={index} />
                  ))}
                </div>
              </div>
            )
          }

          return (
            <InfiniteList
              items={allProposals}
              loadingState={proposalsListLoadingState}
              hasNextPage={proposalsQuery.hasNextPage}
              errorTitle={proposalsErrorTitle}
              noDataTitle='Proposals not found'
              onRetry={() => proposalsQuery.refetch()}
              onLoadNext={() => proposalsQuery.fetchNextPage()}
            >
              <div className='space-y-4'>
                <div className='space-y-3'>
                  <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
                    <div className='flex-1'>
                      <h2 className='text-foreground text-lg font-semibold'>
                        Proposal history
                      </h2>
                      <p className='text-muted-foreground text-sm'>
                        Proposals for this multisig group
                      </p>
                    </div>

                    <div className='flex items-center gap-3'>
                      {(() => {
                        if (allProposals.length === 0) return null
                        return (
                          <p className='text-muted-foreground text-sm'>
                            {sortedAndFilteredProposals.length} of{' '}
                            {allProposals.length}
                          </p>
                        )
                      })()}

                      <UiSelect
                        value={statusFilter}
                        onValueChange={value =>
                          setStatusFilter(
                            value as MultisigProposalStatus | 'ALL',
                          )
                        }
                      >
                        <UiSelectTrigger className='text-foreground w-35'>
                          <UiSelectValue placeholder='Filter status' />
                        </UiSelectTrigger>
                        <UiSelectContent>
                          <UiSelectItem value='ALL'>All statuses</UiSelectItem>
                          <UiSelectItem value='SUBMITTED'>
                            Submitted
                          </UiSelectItem>
                          <UiSelectItem value='ACCEPTED'>Accepted</UiSelectItem>
                          <UiSelectItem value='REJECTED'>Rejected</UiSelectItem>
                          <UiSelectItem value='EXECUTED'>Executed</UiSelectItem>
                          <UiSelectItem value='FAILED'>Failed</UiSelectItem>
                        </UiSelectContent>
                      </UiSelect>
                    </div>
                  </div>
                  <UiSeparator />
                </div>

                {(() => {
                  if (sortedAndFilteredProposals.length > 0) {
                    return sortedAndFilteredProposals.map(proposal => (
                      <ProposalCard key={proposal.id} proposal={proposal} />
                    ))
                  }

                  if (allProposals.length > 0 && statusFilter !== 'ALL') {
                    return (
                      <UiCard className='p-6 text-center'>
                        <p className='text-foreground text-sm font-semibold'>
                          No proposals with this status
                        </p>
                        <p className='text-muted-foreground mt-2 text-xs'>
                          Try selecting a different status filter
                        </p>
                      </UiCard>
                    )
                  }

                  if (proposalsQuery.hasNextPage) {
                    return (
                      <div className='space-y-4'>
                        {Array.from({ length: 12 }).map((_, index) => (
                          <ProposalCardSkeleton key={index} />
                        ))}
                      </div>
                    )
                  }

                  return (
                    <UiCard className='p-6 text-center'>
                      <p className='text-foreground text-sm font-semibold'>
                        Proposals not found
                      </p>
                    </UiCard>
                  )
                })()}
              </div>
            </InfiniteList>
          )
        })()}
      </div>
    </UiContainer>
  )
}
